/**
 * F 374 — THE DEFAULT AUTH MODULE. Session identity, the user node, and the
 * passphrase→key derivation.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The credential store had exactly ONE caller-supplied key path, and it was the
 * environment secret. `envKey.ts` says so, and it is right — but the environment
 * secret in a Vite client build is a `VITE_*` variable, and Vite **inlines those
 * into the public bundle**. Measured on the live site on 2026-10-20:
 *
 *     $ curl -s https://freshcards.freshvibeapps.com/assets/index-1B6bQvxb.js \
 *         | grep -o 'VITE_FC_CREDENTIAL_SECRET:"[^"]*"'
 *     VITE_FC_CREDENTIAL_SECRET:"0tPEvY9HipalZUN3GXIXxV5TLtKoNBnTrHSW1Fyi..."
 *
 * So the key that seals every stored credential was **downloadable by anyone**.
 * The encryption at rest was real encryption with a public key. A password that
 * never leaves the browser is not public. That is the whole reason this module
 * exists, and it is the reason the passphrase path is stronger rather than
 * merely different.
 *
 * WHAT THIS IS, AND WHAT IT IS NOT
 * --------------------------------
 * It is a LOCAL UNLOCK, not authentication. There is no server to check a
 * password against — and the dispatch forbids sending one ("the password is
 * never sent to the server"), so there cannot be. What the password buys is
 * three real things:
 *
 *   1. a KEY that seals the credential at rest, and that only this browser knows;
 *   2. an IDENTITY — a `user` node in the graph — so credentials are scoped to
 *      a person rather than to a machine-wide `operator-1`;
 *   3. a BOUNDARY: the passphrase lives in memory for the life of the session
 *      and is never written to disk, never written to a log, never transmitted.
 *
 * What it does NOT buy is proof of identity. Anyone who can reach the app can
 * type any email and any password and get a local session. The report says so
 * plainly rather than calling this "login security".
 *
 * THE SALT IS DETERMINISTIC, AND IT IS BOUND TO THE PRINCIPAL
 * -----------------------------------------------------------
 * A credential sealed today must be openable tomorrow, so the salt cannot be
 * random per call. It is bound to the PRINCIPAL ID, not to the password, so:
 *
 *   - the same person always derives the same key (a credential survives a
 *     reload);
 *   - two people with the SAME password derive DIFFERENT keys, so one person
 *     can never read another's credential;
 *   - the salt is public by construction, which is fine: a KDF salt is not a
 *     secret. The password is the secret.
 *
 * It MUST be base64. `deriveKeyPBKDF2` calls `base64ToArrayBuffer(salt)`, and
 * an arbitrary string throws `InvalidCharacterError` inside `atob` — a failure
 * that points at the KDF rather than at the caller. That trap is already
 * documented in `envKey.ts`; this file does not walk into it a second time.
 */

import type { User } from '../../types/models/User';
import { UserStore } from '../users/UserStore';
import type { StorageAdapter } from '../storage/types';
import { LocalStorageAdapter } from '../storage/adapters/LocalStorageAdapter';
import {
  keyFromPassphrase,
  type KeyResolver,
  type Principal,
} from '../credentials/credentialStore';

/** The dev bypass identity. A local convenience, and named in one place. */
export const DEV_BYPASS_EMAIL = 'dev@freshcards.local';
export const DEV_BYPASS_PASSWORD = 'pineapple';

/** The key length AES-256 needs. A short key is a silent security downgrade. */
const DERIVED_KEY_BYTES = 32;

/**
 * The key-schedule label. PBKDF2 rather than Argon2id, for the same reason
 * `envKey.ts` gives: the Argon2 WASM path is a best-effort fallback in this
 * substrate, and a derivation that can be absent would make the store
 * unreadable for reasons that have nothing to do with the password.
 */
const PASSPHRASE_KDF = 'pbkdf2' as const;

/**
 * The two environment signals the dev bypass reads.
 *
 * They are injectable so the DECISION can be tested without stubbing a
 * browser global. `window.location` is unforgeable-ish in jsdom — a plain
 * assignment hits the navigation setter, jsdom declines to navigate, and the
 * assignment appears to succeed while changing nothing. And `import.meta.env`
 * is a per-module object under vitest's SSR transform, so mutating it in a
 * test does not reliably reach the module under test. Injecting the signals
 * tests the real decision instead of the plumbing around it.
 */
export interface DevEnvironmentSignals {
  /** `import.meta.env.DEV` — Vite's own build flag. */
  dev?: boolean;
  /** `window.location.hostname`. */
  hostname?: string;
}

/**
 * Is this a dev build?
 *
 * Two independent signals, because they fail independently: `import.meta.env.DEV`
 * is Vite's own flag and is false in every production bundle, while the
 * hostname check catches a production bundle served from a laptop.
 *
 * The two are OR-ed, and that is the dispatch's rule verbatim: "when the
 * environment is dev OR the hostname is localhost". A dev build is a dev build
 * wherever it is served from, and a `localhost` host is a local session
 * wherever it was built.
 */
export function isDevEnvironment(signals?: DevEnvironmentSignals): boolean {
  let dev = signals?.dev;
  let hostname = signals?.hostname;

  if (dev === undefined) {
    try {
      const env = (import.meta as unknown as { env?: { DEV?: boolean } }).env;
      dev = env?.DEV === true;
    } catch {
      // `import.meta` is not available in every host (a CJS test harness, for
      // one). A missing flag is not a dev signal.
      dev = false;
    }
  }
  if (hostname === undefined && typeof window !== 'undefined') {
    hostname = window.location?.hostname;
  }

  return dev === true || hostname === 'localhost';
}

/**
 * Should the app skip the login screen and sign in as the dev user?
 *
 * The bypass is a LOCAL convenience. It is a function of the ENVIRONMENT, not
 * of anything the caller passes, so a page cannot ask for it.
 */
export function devBypassEnabled(signals?: DevEnvironmentSignals): boolean {
  return isDevEnvironment(signals);
}

/** The principal a user node becomes. */
export function principalForUser(user: User): Principal {
  const principal: Principal = { scope: 'user', id: user.id };
  if (typeof user.email === 'string' && user.email.length > 0) {
    (principal as Principal & { email: string }).email = user.email;
  }
  return principal;
}

/**
 * Normalise an email into the shape the user node stores.
 *
 * Lowercased and trimmed, because `Ada@Example.com` and `ada@example.com` are
 * the same person and must not become two user nodes. The `@` is NOT stripped:
 * the id is derived from the name, and the name IS the email, so two people at
 * `example.com` must not collide.
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * The storage the user node lives in.
 *
 * WHY THE LOCAL STORAGE ADAPTER, AND NOT THE INDEXEDDB ONE
 * --------------------------------------------------------
 * This started as `src/core/storage/adapters/IndexedDBAdapter.ts`, and the
 * BROWSER proved it wrong where the unit test could not.
 *
 * That adapter opens the database `freshcards` and the object store
 * `freshcards` — the SAME database and store that
 * `src/core/storage-adapter.ts` creates, and it creates them WITHOUT the
 * `byKey` index. `IndexedDBAdapter.list()` does
 * `store.index('byKey').getAllKeys(range)`, and `createIndex` only ever runs
 * inside `onupgradeneeded` — which only fires when the version increases.
 * The database already exists at version 1, so the upgrade never runs, the
 * index never exists, and `list()` throws:
 *
 *     Failed to execute 'index' on 'IDBObjectStore':
 *     The specified index was not found.
 *
 * That is a PRE-EXISTING defect in that adapter — two adapters claiming one
 * database and one object store with incompatible schemas, where whichever
 * opens first wins. It is not this dispatch's file to change, so it is
 * reported rather than edited.
 *
 * `LocalStorageAdapter` (from the same `src/core/storage/adapters/` folder)
 * has none of that: it implements the SAME eleven-member `StorageAdapter`
 * contract that `UserStore` and `QueryLayer` both speak, its `list()` is a
 * plain key scan with no index, and it writes into the `freshcards:` key
 * space — which is exactly the key space the app's own key/value graph layer
 * (`src/core/storage-adapter.ts`, the `storage` singleton) reads and writes.
 *
 * So the user node is not in a private corner: it is at
 * `freshcards:user:<id>`, the same place the app's own graph commands put
 * things, and `QueryLayer.list('user')` finds it.
 */
let sharedAdapter: StorageAdapter | null = null;

export async function getAuthStorage(): Promise<StorageAdapter> {
  if (!sharedAdapter) {
    sharedAdapter = new LocalStorageAdapter();
  }
  return sharedAdapter;
}

/** Test seam: drop the cached adapter so a test can start from a clean one. */
export function __resetAuthStorageForTests(): void {
  sharedAdapter = null;
}

/**
 * The user node for an email, created if it is absent.
 *
 * ABSENCE IS CHECKED IN STORAGE, not in a map. `UserStore` keeps its rows in
 * an in-memory map that is populated from `initial` — a fresh store has an
 * empty map even when the row is on disk, so asking the store "does this user
 * exist?" would answer "no" for a user who has signed in before, and the node
 * would be rewritten on every login. The graph's own listing convention
 * (`list('user:')` → `load(key)`) is the check that is actually true.
 */
export async function ensureUserNode(
  email: string,
  storage?: StorageAdapter,
): Promise<{ user: User; created: boolean }> {
  const normalized = normalizeEmail(email);
  if (!normalized) {
    throw new Error('[auth] a user needs an email address.');
  }
  const store = storage ?? (await getAuthStorage());

  const keys = await store.list('user:');
  for (const key of keys) {
    const row = await store.load(key);
    if (!row || typeof row !== 'object') continue;
    const candidate = row as User;
    if (candidate.email && normalizeEmail(candidate.email) === normalized) {
      return { user: candidate, created: false };
    }
  }

  // The NAME is the email, and that is deliberate. `User.ts` derives the id
  // from the name ONLY, on the grounds that "an id that moves when an optional
  // field is filled in is not an id". Deriving the id from the email-as-name
  // keeps the id stable whether or not `email` is set, and keeps two people
  // with the same local part at the same domain apart.
  const users = new UserStore({ storage: store });
  const result = await users.createUserRecord({
    name: normalized,
    email: normalized,
    label: normalized,
  });
  // The `in` test narrows the union in a way that does not depend on
  // `strictNullChecks` — this repo builds with `strict: false`, and under that
  // setting a truthiness test on a boolean-literal discriminant does not
  // narrow the way a `=== false` comparison does.
  if (!('user' in result)) {
    throw new Error(`[auth] the user node could not be created: ${result.message}`);
  }
  return { user: result.user, created: result.created };
}

/**
 * The deterministic, base64 salt for a principal.
 *
 * Base64 because `deriveKeyPBKDF2` decodes it. Deterministic because a
 * credential sealed today must open tomorrow. Bound to the principal because
 * two people must not share a key.
 */
export function passphraseSalt(principalId: string): string {
  // `btoa` is latin1-only, and a principal id is not guaranteed to be latin1
  // in principle. Encoding the bytes first keeps this total.
  const label = `fcr.passkey.v1.${principalId}`;
  const bytes = new TextEncoder().encode(label);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

/**
 * Derive the AES-256 key a passphrase stands for, for ONE principal.
 *
 * When `saltB64` is supplied the caller is asserting THIS is the salt that
 * was recorded alongside the ciphertext at seal time. That is the salt the
 * VPS bridge replays, and it is what makes the round-trip work without
 * leaking the principal id into the resolver signature.
 *
 * When `saltB64` is absent we fall back to `passphraseSalt(principalId)` so
 * callers that built their key outside the credential store (most of the
 * test suite) keep working without changes.
 *
 * Returns base64 of exactly 32 bytes — the shape `encryptWithKeyFor` imports
 * as an AES-GCM key. A resolver that returned anything else fails inside
 * `atob` with `InvalidCharacterError`, which presents as "the cipher broke"
 * rather than "the resolver returned the wrong shape".
 */
export async function derivePassphraseKey(
  passphrase: string,
  principalId: string,
  saltB64?: string,
): Promise<string> {
  if (!passphrase) {
    throw new Error('[auth] a passphrase is required to derive a key.');
  }
  const salt = saltB64 ?? passphraseSalt(principalId);
  const key = await keyFromPassphrase(passphrase, salt, PASSPHRASE_KDF);
  const bytes = Math.floor((key.replace(/=+$/, '').length * 3) / 4);
  if (bytes < DERIVED_KEY_BYTES) {
    // The LENGTH is safe to report. The passphrase never is.
    throw new Error(
      `[auth] the passphrase derived only ${bytes} key bytes, need ${DERIVED_KEY_BYTES}.`,
    );
  }
  return key;
}

/**
 * A `KeyResolver` backed by the user's passphrase.
 *
 * The salt comes from the caller — the per-record random salt the store
 * recorded at `set()` and reads back at `resolve()`. A caller that DOES NOT
 * pass a salt falls back to the legacy `passphraseSalt(principalId)` so
 * existing keys, which are bound to the label-salt, keep reading.
 *
 * This is the only seal path that survives a third party — the env-secret
 * path uses a fixed label that nothing outside the process can match —
 * and it is what the VPS bridge replays byte-for-byte when its
 * `LANE_BRIDGE_CREDENTIAL_PASSPHRASE` is set to the same passphrase the user
 * typed into the unlock screen. The salt on the wire is now the same salt
 * both sides see.
 */
export function keyFromUserPassphrase(opts: { passphrase: string }): KeyResolver {
  // ASYNC, and that is load-bearing rather than style. `KeyResolver` is
  // declared as returning `Promise<string> | string`, so a resolver that
  // THROWS synchronously is a type lie: a caller that does
  // `resolver(p).catch(...)` — or a test that does `expect(...).rejects` — never
  // receives a promise, because the throw happens before the call returns.
  // Making it `async` turns every failure into a rejection, which is the
  // shape the type promises.
  return async function resolve(principal: Principal, saltB64?: string): Promise<string> {
    const id = principal?.id;
    if (!id) {
      throw new Error('[auth] the credential store asked for a key with no principal.');
    }
    return derivePassphraseKey(opts.passphrase, id, saltB64);
  };
}

/**
 * F 441 — THE AGENT VIEW TOKEN.
 *
 * Lets an automated browser (a Playwright run, a screenshot job) see the UI
 * without a password, without the operator ever handing over their password.
 *
 * WHY THE TOKEN IS NEVER COMPARED IN THE BROWSER
 * ------------------------------------------------
 * The obvious implementation is to read a `VITE_AGENT_VIEW_TOKEN` in the
 * browser and compare `?token=` against it. That is the F 374 disaster again,
 * and this module's own header is the evidence: Vite **inlines** `VITE_*` into
 * the public bundle. `curl` the JS and the token is readable by anyone, so the
 * bypass stops being a bypass and becomes a permanently unlocked front door on
 * every browser on earth.
 *
 * So the client does not know the token. It sends the token it was given to
 * `/api/fcc/agent-view`, which already holds the real value in
 * `FCC_KV_TOKEN`, and gets back a boolean. The secret never enters the page.
 *
 * WHAT IT BUYS AND WHAT IT DOES NOT
 * ---------------------------------
 * It unlocks the UI only. Graph reads still go through `/kv` behind the build's
 * `FCC_KV_TOKEN`, so an agent-view session renders the workspace and sees
 * whatever the browser can already reach — which on the production build is
 * nothing, because that build carries no graph token. Anyone with a real
 * account's data already has their own login; this is a VIEW, deliberately.
 *
 * FAIL CLOSED, ALWAYS. A missing token, a wrong token, a network error, a
 * server that does not know about this endpoint — every one of them returns
 * false, and the login screen stands. There is no partial state in which the
 * bypass is "probably fine".
 */

/** Where the token was found, and whether it should be remembered. */
export interface AgentViewProbe {
  ok: boolean;
  /** The token as presented, or '' when there was none. Never stored. */
  token: string;
}

const AGENT_VIEW_ENDPOINT = '/api/fcc/agent-view';

/**
 * Read `?token=` off the current URL.
 *
 * Returns '' rather than throwing in a non-browser host, because this is
 * called from an effect and a CJS test harness has no `window`.
 */
export function tokenFromLocation(search?: string): string {
  let qs = search;
  if (qs === undefined) {
    if (typeof window === 'undefined') return '';
    qs = window.location?.search ?? '';
  }
  try {
    // `URLSearchParams` decodes percent-encoding, so a token containing `+` or
    // `=` survives a round trip through a query string.
    return new URLSearchParams(qs).get('token') ?? '';
  } catch {
    return '';
  }
}

/**
 * Ask the server whether this token opens the view.
 *
 * Returns `{ ok: false }` for every failure mode — wrong token, no token,
 * offline, non-2xx, malformed JSON. The caller cannot distinguish "wrong" from
 * "unreachable", which is deliberate: the browser has no business branching on
 * why the server said no, and a distinguishable failure is a failure an
 * attacker can probe.
 */
export async function verifyAgentViewToken(
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AgentViewProbe> {
  if (!token) return { ok: false, token: '' };
  try {
    const res = await fetchImpl(
      `${AGENT_VIEW_ENDPOINT}?token=${encodeURIComponent(token)}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    );
    // 401 IS the expected answer for a bad token — not an exception.
    if (!res.ok) return { ok: false, token };
    const body = (await res.json()) as { ok?: unknown; agent_view?: unknown };
    return { ok: body?.ok === true && body?.agent_view === true, token };
  } catch {
    return { ok: false, token };
  }
}

/**
 * Should this session skip the login screen?
 *
 * True only when the server said so. There is no local fallback and no
 * "close enough" comparison — the browser is not the authority here.
 */
export function agentViewAllowed(probe: AgentViewProbe | null | undefined): boolean {
  return probe?.ok === true;
}
